/**
 * Freeze V2 — Day Filter Hook
 *
 * Provides D7/D6.5/All filter buttons that filter the visible embryos
 * in detail grids and auto-expand matching fertilization rows.
 */
import { useCallback, useState } from 'react';

import type { GridApi } from 'ag-grid-community';

import type { FertAndEmbryosResponse } from '@/services/freeze';

import { useFreezeSelection } from '../contexts';

export type DayFilter = 'all' | 'd7' | 'd6.5';

type UseDayFilterParams = {
  gridApi: GridApi | null;
  embryosByFert: Map<string, FertAndEmbryosResponse>;
};

export function useDayFilter({ gridApi, embryosByFert }: UseDayFilterParams) {
  const [activeDayFilter, setActiveDayFilter] = useState<DayFilter>('all');
  const { dispatch: selectionDispatch } = useFreezeSelection();

  const applyFilter = useCallback(
    (filter: DayFilter) => {
      setActiveDayFilter(filter);

      if (!gridApi) return;

      // Collapse all first
      gridApi.forEachNode((node) => {
        if (node.expanded) {
          node.setExpanded(false);
        }
      });

      if (filter === 'all') {
        // Expand all that have embryos, and clear the day-based selection (parity with v1).
        gridApi.deselectAll();
        selectionDispatch({ type: 'CLEAR' });
        gridApi.forEachNode((node) => {
          const fertId = node.data?.fertId;
          if (fertId && embryosByFert.has(fertId)) {
            const data = embryosByFert.get(fertId)!;
            if (data.liveEmbryos.length > 0 || data.tempEmbryos.length > 0) {
              node.setExpanded(true);
            }
          }
        });
        return;
      }

      // Day filter (D7/D6.5): expand matching rows and select the matching live
      // embryos + their parent ferts (parity with v1 useDaySelection).
      const matchingLiveIds: string[] = [];
      const fertIdsWithDay = new Set<string>();

      gridApi.forEachNodeAfterFilter((node) => {
        const fertId = node.data?.fertId;
        if (!fertId) return;
        const data = embryosByFert.get(fertId);
        if (!data) return;

        const dayLive = data.liveEmbryos.filter((e) =>
          filter === 'd7' ? e.d7 === 1 : e.d65 === 1,
        );
        if (dayLive.length > 0) {
          fertIdsWithDay.add(fertId);
          for (const e of dayLive) {
            if (e.id) matchingLiveIds.push(e.id);
          }
        }
      });

      gridApi.deselectAll();
      gridApi.forEachNode((node) => {
        const fertId = node.data?.fertId;
        if (fertId && fertIdsWithDay.has(fertId)) {
          node.setExpanded(true);
          node.setSelected(true);
        }
      });

      // Selecting master rows above fires SET_FERT_IDS; set the live selection directly.
      selectionDispatch({ type: 'SET_LIVE_IDS', ids: matchingLiveIds });
    },
    [gridApi, embryosByFert, selectionDispatch],
  );

  return {
    activeDayFilter,
    applyDayFilter: applyFilter,
  };
}
